import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { HorizontalBar } from 'react-chartjs-2'

interface SnagPriorityChartProps {
  byPriority: Array<{ priority: string; count: number }>
  total: number
}

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: '#ef4444',
  HIGH: '#f97316',
  MEDIUM: '#eab308',
  LOW: '#22c55e',
}

const PRIORITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']

const SnagPriorityChart: React.FC<SnagPriorityChartProps> = ({ byPriority, total }) => {
  const { t } = useTranslation()

  if (total === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{t('Dashboard_snag_chart_title', 'Snags by Priority')}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[200px]">
          <p className="text-sm text-muted-foreground">{t('Dashboard_no_snags', 'No snags yet')}</p>
        </CardContent>
      </Card>
    )
  }

  // Sort by priority order and fill missing
  const priorityMap = Object.fromEntries(byPriority.map((p) => [p.priority, p.count]))
  const sorted = PRIORITY_ORDER.filter((p) => priorityMap[p])

  const labels = sorted.map((p) => p.charAt(0) + p.slice(1).toLowerCase())
  const data = sorted.map((p) => priorityMap[p] || 0)
  const backgroundColor = sorted.map((p) => PRIORITY_COLORS[p] || '#94a3b8')

  const chartData = {
    labels,
    datasets: [
      {
        data,
        backgroundColor,
        borderWidth: 0,
        barThickness: 18,
      },
    ],
  }

  const options = {
    legend: { display: false },
    scales: {
      xAxes: [{ ticks: { beginAtZero: true, stepSize: 1 }, gridLines: { display: false } }],
      yAxes: [{ gridLines: { display: false } }],
    },
    tooltips: { enabled: true },
    maintainAspectRatio: true,
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{t('Dashboard_snag_chart_title', 'Snags by Priority')}</CardTitle>
      </CardHeader>
      <CardContent>
        <HorizontalBar data={chartData} options={options} />
      </CardContent>
    </Card>
  )
}

export default SnagPriorityChart
